import {
  ContractInfo,
  Holder,
  HolderProps,
} from 'api/types/httpsTypes/contracts';

import {
  EmptyPageDex,
  ErrorPageDex,
} from '../../research/components/shared/Common';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import { thDynamicClass } from '../../research/components/SmartSwaps';
import { TableColumn } from 'modules/shared-components/data-display/table/types';
import Tooltip from 'modules/shared-components/tooltip';
import { EthAddress } from 'modules/ethereum/components/EthAddress';
import { chainAssetImg, Chains } from 'api/types/httpsTypes/d-wallets';
import { walletLabels } from '../../research/components/Wallets/columns';
import { WalletPeriods } from '../../research/components/Wallets/usePageParams';
import { formatNumberWithSuffix, safeBig } from 'utils/FormatNumber';
import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import ErrorBoundary from 'components/error-boundaries';
import { useWebsocket } from 'api/hooks/useWebsocket';
import { useTokenPrice } from 'api/hooks/useTokenPrice';
import { useChainInfo } from 'api/hooks/useChainInfo';
import { PerformanceStyle } from '../../components/alerts/performanceStyle';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import ProgressBar from 'modules/shared-components/data-display/ProgressBar';

interface Props {
  contract: ContractInfo;
  mainData: HolderProps | undefined;
  error: any;
  isLoading: boolean;
}

const AmountComponent = ({
  data,
  contract,
}: {
  data: Holder;
  contract: ContractInfo;
}) => {
  const tokenPrice = useTokenPrice(
    contract.generalInfo.chain.key,
    contract.generalInfo.address
  );

  const chainAssetPrice = useChainInfo(contract.generalInfo.chain.key)?.price;

  const value = chainAssetPrice
    ? safeBig(data.amount)
        .mul(
          tokenPrice ??
            contract.liquidityInfo.activePools?.[0]?.priceDollar ??
            0
        )
        .div(chainAssetPrice)
    : null;

  return (
    <div className="text-xs">
      <div className="space-y-1 items-center">
        <div className="space-y-1 items-center">
          <div className="xxs:text-sm">
            <PerformanceStyle
              className={twMerge('xxs:text-sm')}
              pnl={safeBig(value).toString()}
              formatFn={formatNumberWithSuffix}
              isOnlyFlashEnabled={true}
              noAsset={true}
              prefix={
                <img
                  src={chainAssetImg(Chains.Solana)}
                  width={10}
                  className="inline-flex xxs:pb-0.5 xxs:mr-1"
                  alt="chain asset logo"
                />
              }
            />
          </div>

          <div className="xxs:text-xs">
            {formatNumberWithSuffix(safeBig(data.amount).toNumber())}{' '}
          </div>
        </div>
      </div>
    </div>
  );
};

const ValueComponent = ({
  data,
  contract,
  className,
}: {
  data: Holder;
  contract: ContractInfo;
  className?: string;
}) => {
  const tokenPrice = useTokenPrice(
    contract.generalInfo.chain.key,
    contract.generalInfo.address
  );


  const value = safeBig(data.amount).mul(
    tokenPrice ?? contract.liquidityInfo.activePools?.[0]?.priceDollar ?? 0
  );


  const percentage = contract.generalInfo.totalSupply
    ? safeBig(data.amount)
        .div(contract.generalInfo.totalSupply)
        .mul(100)
        .toFixed(2)
    : null;

  return (
    <div className="text-xs">
      <div className="space-y-1 items-center">
        <div className="flex space-x-1 items-center">
          <div className="xxs:text-sm">
            <PerformanceStyle
              className={twMerge('xxs:text-sm', className)}
              pnl={safeBig(value).toString()}
              formatFn={formatNumberWithSuffix}
              isOnlyFlashEnabled={true}
            />
          </div>
          {contract.generalInfo.totalSupply && (
            <Tooltip
              text={
                'This wallet is holding ' +
                percentage +
                '% of the total supply.'
              }
            >
              <div className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:p-1 rounded">
                {percentage}%
              </div>
            </Tooltip>
          )}
        </div>

        <ProgressBar
          fillPercentage={percentage ? Number(percentage) : 0}
          className="xxs:h-[5px] lg:max-w-[100px] xxs:bg-dex-black-600"
        />
      </div>
    </div>
  );
};

const BalanceComponent = ({ data }: { data: Holder }) => {
  return (
    <div className="text-xs">
      <div className="space-y-1 items-center">
        <div className="flex space-x-1 items-center">
          <img
            src={chainAssetImg(Chains.Solana)}
            width={10}
            alt="chain asset logo"
          />
          <PerformanceStyle
            className="xxs:text-sm"
            noAsset={true}
            pnl={safeBig(data.solBalance).toString()}
            formatFn={formatNumberWithSuffix}
            isOnlyFlashEnabled={true}
          />
        </div>
      </div>
    </div>
  );
};

export const desktopColumns = (
  contract: ContractInfo
): RowColumnProps<Holder>[] => {
  return [
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The wallet holdings ranking.">
            <div>Ranking</div>
          </Tooltip>
        </div>
      ),
      key: 'RANKING',
      component: ({ data }) => (
        <div className="flex xxs:space-x-1 items-center">
          <div className="">{data.rank}</div>
        </div>
      ),
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The wallet address holding the token.">
            <div>Wallet</div>
          </Tooltip>
        </div>
      ),
      key: 'WALLET',
      component: ({ data }) => (
        <div className="flex items-center xxs:gap-2">
          <div className="max-w-[120px] overflow-hidden truncate">
            <EthAddress
              address={data.address}
              precision={3}
              kind="account"
              chain={Chains.Solana}
            />
          </div>
          {data.address ===
          contract.liquidityInfo.activePools[0].poolAddress ? (
            <div className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:p-1 rounded xxs:text-xs">
              Liq. Pool
            </div>
          ) : (
            ''
          )}
          {walletLabels({ walletLabels: data.labels }, WalletPeriods.Thirty)}
        </div>
      ),
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The current available SOL Balance.">
            <div>Balance</div>
          </Tooltip>
        </div>
      ),
      key: 'SOL BALANCE',
      component: (props) => <BalanceComponent {...props} />,
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The current holding amount in SOL and tokens.">
            <div>Amount</div>
          </Tooltip>
        </div>
      ),
      key: 'AMOUNT',
      component: (props) => <AmountComponent {...props} contract={contract} />,
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The current holding value in USD and the holding share.">
            <div>Value</div>
          </Tooltip>
        </div>
      ),
      key: 'VALUE',
      component: (props) => <ValueComponent {...props} contract={contract} />,
    },
  ];
};

const MobileComponent = ({
  data,
  contract,
}: {
  data: Holder;
  contract: ContractInfo;
}) => {
  return (
    <div className="flex justify-between items-center">
      <div className="flex flex-col space-y-1">
        <div className=" flex xxs:space-x-1 items-center">
          <div className="max-w-[120px] overflow-hidden truncate">
            <EthAddress
              address={data.address}
              precision={3}
              kind="account"
              chain={Chains.Solana}
            />
          </div>

          {data.address ===
          contract.liquidityInfo.activePools[0].poolAddress ? (
            <div className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:p-1 rounded xxs:text-xs">
              Liq. Pool
            </div>
          ) : (
            ''
          )}
        </div>
        <div className="flex xxs:space-x-1 items-center">
          <div className="flex space-x-1 items-center justify-end">
            <img
              src={chainAssetImg(Chains.Solana)}
              width={10}
              alt="chain asset logo"
            />
            <PerformanceStyle
              className="xxs:text-sm"
              noAsset={true}
              pnl={safeBig(data.solBalance).toString()}
              formatFn={formatNumberWithSuffix}
              isOnlyFlashEnabled={true}
            />
          </div>
          {walletLabels({ walletLabels: data.labels }, WalletPeriods.Thirty)}
        </div>
      </div>

      <div className="xxs:space-y-1">
        <ValueComponent
          contract={contract}
          data={data}
          className="xxs:text-lg"
        />
      </div>
    </div>
  );
};

export const mobileColumns = (
  contract: ContractInfo
): RowColumnProps<Holder>[] => {
  return [
    {
      key: 'WALLET',
      component: (props) => <MobileComponent {...props} contract={contract} />,
    },
  ];
};

export const HoldersComponent = ({
  contract,
  mainData,
  error,
  isLoading,
}: Props) => {
  const data = mainData?.holders;
  const isLargeScreen = useMediaQuery(screenGte.large);
  const columns = isLargeScreen
    ? desktopColumns(contract)
    : mobileColumns(contract);

  const tokenPriceSub = useMemo(
    () => ({
      chain: contract.generalInfo.chain.key,
      address: contract.generalInfo.address,
    }),
    [contract.generalInfo.chain.key, contract.generalInfo.address]
  );

  useWebsocket('token_price', tokenPriceSub);

  return error ? (
    <ErrorPageDex />
  ) : (
    <div className="overflow-x-auto">
      <div className="w-full lg:px-2">
        <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
          <Header
            trClassName="lg:grid lg:grid-cols-[100px_1fr_1fr_1fr_1fr]
 xxs:dark:bg-dex-black-800 border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-600"
            columns={columns as TableColumn<any>[]}
            thDynamicClass={thDynamicClass}
            rowClassName="xxs:p-1 xxs:text-dex-white-secondary hover:text-dex-white"
            className="xxs:dark:bg-dex-black-800 hover:text-dex-white xxs:text-xs lg:grid"

          />
          <tbody>
            {isLoading ? (
              [...Array(10).keys()]
                .map((i) => i + 1)
                .map((index) => (
                  <Table.LoadingRow
                    thDynamicClass={thDynamicClass}
                    key={index}
                    className="lg:grid lg:grid-cols-[100px_1fr_1fr_1fr_1fr]
 xxs:bg-dex-black-700"
                    columns={columns as TableColumn<any>[]}
                  />
                ))
            ) : data?.length === 0 ? (
              <EmptyPageDex height="50px" title="No Smart Swaps Found" />
            ) : (
              data?.map((item, rowId) => (
                <TableRow
                  key={item.address + '_' + rowId}
                  data={item}
                  columnsDef={columns}

                  className=" lg:grid-cols-[100px_1fr_1fr_1fr_1fr] cursor-default	"

                />
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

interface TableProps {
  columnsDef: RowColumnProps<Holder>[];
  data: Holder;
  className?: string;
}

export interface RowColumnProps<T> {
  label?: string | JSX.Element;
  key: string;
  sortableKey?: string;
  className?: string;
  showOnMobile?: boolean;
  component?: (props: { data: T }) => JSX.Element;
}

export const TableRow: FC<TableProps> = ({ data, columnsDef, className }) => {
  return (
    <tr
      className={twMerge(
        'border-t border-dex-black-700 lg:dark:bg-dex-black-800 items-center lg:grid lg:grid-cols-3',
        className
      )}
    >
      <ErrorBoundary>
        {columnsDef.map((column) => {
          const Column = column.component;
          if (!Column) return;
          return (
            <Table.Td className={thDynamicClass(column.key)} key={column.key}>
              <Column data={data} />
            </Table.Td>
          );
        })}
      </ErrorBoundary>
    </tr>
  );
};
